import { useEffect, useState } from "react";
import { DollarSign, MousePointerClick, TrendingUp, Users } from "lucide-react";
import { api } from "@/lib/api";

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-[#281245] bg-[#0b0514] p-6" data-testid={testid}>
    <p className="font-mono-j flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#a78bce]"><Icon className="h-3.5 w-3.5" /> {label}</p>
    <p className="font-display mt-3 text-4xl text-[#f3eefc]">{value}</p>
    {sub && <p className="mt-1 text-xs text-[#a78bce]">{sub}</p>}
  </div>
);

export const AnalyticsTab = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => setD({ revenue: {}, conversion: {}, traffic: [], items: [] }));
  }, []);

  if (!d) return <p className="font-mono-j py-16 text-center text-xs tracking-[0.3em] text-[#a78bce]">Loading analytics…</p>;
  const conv = d.conversion || {};
  const rev = d.revenue || {};
  const maxT = Math.max(1, ...(d.traffic || []).map((t) => t.count));
  const maxV = Math.max(1, ...(d.items || []).map((i) => i.views));

  return (
    <div className="space-y-10" data-testid="analytics-tab">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card icon={DollarSign} label="Total revenue" value={`$${Number(rev.total || 0).toLocaleString("en-CA")}`} sub={`${rev.completed || 0} items delivered`} testid="stat-revenue-total" />
        <Card icon={DollarSign} label="This month" value={`$${Number(rev.this_month || 0).toLocaleString("en-CA")}`} sub="CAD, from completed claims" testid="stat-revenue-month" />
        <Card icon={MousePointerClick} label="Claim conversion" value={`${conv.rate ?? 0}%`} sub={`${conv.claims || 0} claims on ${conv.product_views || 0} item views`} testid="stat-conversion" />
        <Card icon={TrendingUp} label="Claim funnel" value={`${conv.claims || 0}/${conv.product_views || 0}`} sub={`${conv.claim_starts || 0} opened the claim form · ${conv.completed || 0} completed`} testid="stat-funnel" />
      </div>

      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <p className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">Traffic sources</p>
          {!d.traffic?.length ? <p className="py-10 text-sm text-[#cbbbe6]" data-testid="traffic-empty">No visits logged yet — sources appear as people browse.</p> : (
            <ul className="mt-5 space-y-3">
              {d.traffic.map((t) => (
                <li key={t.source} data-testid={`traffic-source-${t.source}`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#f3eefc] capitalize">{t.source === "instagram" ? "Instagram" : t.source}</span>
                    <span className="font-mono-j text-[#a78bce]">{t.count}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#281245]">
                    <div className="h-full rounded-full bg-[#a855f7]" style={{ width: `${(t.count / maxT) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-6 text-xs text-[#6b5a87]">Sources come from the link people click (Instagram, Google…) and tagged links like ?utm_source=instagram.</p>
        </div>
        <div>
          <p className="font-mono-j text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">Items — views → claims</p>
          {!d.items?.length ? <p className="py-10 text-sm text-[#cbbbe6]" data-testid="items-empty">No item activity yet.</p> : (
            <ul className="mt-5 divide-y divide-[#281245] border-y border-[#281245]">
              {d.items.map((i) => (
                <li key={i.product_id} className="py-3" data-testid={`analytics-item-${i.product_id}`}>
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="truncate text-[#f3eefc]">{i.title}</span>
                    <span className="font-mono-j shrink-0 text-[#a78bce]">{i.views} views · {i.claims} claims{i.completed ? ` · ${i.completed} sold` : ""}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#281245]">
                      <div className="h-full rounded-full bg-[#7c3aed]" style={{ width: `${(i.views / maxV) * 100}%` }} />
                    </div>
                    <span className="font-mono-j w-14 text-right text-xs text-[#d8b4fe]">{i.rate}%</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-6 flex items-center gap-2 text-xs text-[#6b5a87]"><Users className="h-3.5 w-3.5" /> Conversion = claims ÷ item views. Claim a view is logged every time an item page opens.</p>
        </div>
      </div>
    </div>
  );
};
